<template>
  <div class="dashboard-container">
    
    <el-card shadow="never" class="filter-card">
      
      <el-row :gutter="20" align="middle">
        <el-col :xs="24" :sm="18">
          <el-button-group class="time-filters">
            <el-button 
              :type="activeTimeType === 'thisMonth' ? 'primary' : ''" 
              @click="setTime('thisMonth')">
              本月
            </el-button>
            <el-button 
              :type="activeTimeType === 'lastMonth' ? 'primary' : ''" 
              @click="setTime('lastMonth')">
              上個月
            </el-button>
            <el-button 
              :type="activeTimeType === 'custom' ? 'primary' : ''" 
              @click="setTime('custom')">
              指定時間
            </el-button>
          </el-button-group>
          
          <el-date-picker
            v-if="activeTimeType === 'custom'"
            v-model="activeTime"
            type="daterange"
            range-separator="至"
            start-placeholder="開始日期"
            end-placeholder="結束日期"
            style="margin-left: 10px; vertical-align: middle;"
          />
        </el-col>
        <el-col :xs="24" :sm="6" class="execute-button-col">
          <el-button type="success" @click="exportReport" style="margin-left: 8px;">
            匯出報表
          </el-button>
        </el-col>
      </el-row>
      
      <el-row :gutter="20" style="margin-top: 20px;">
        <el-col :span="24">
          <span 
            v-for="area in areafilters"
            :key="area.areaName"
          >
            <el-radio v-model="areafiltersSelection" :label="area.areaName" border>{{ area.areaName }}</el-radio>
          </span>
        </el-col>
      </el-row>
    </el-card>

    <div ref="exportRoot" class="export-root">
      <el-card
        v-for="station in stationTable"
        :key="station.stationName"
        class="revenue-card"
        style="margin-top: 20px;"
      >
        <template #header>
          <div>
            <span>{{ station.stationName }}</span>
          </div>
        </template>
      
        <div
          v-for="instrument in station.instruments"
          :key="instrument.name"
          class="instrument-list"
        >
          <div class="instrument-header">
            <div class="instrument-name">{{ instrument.name }}</div>

            <el-space size="6" wrap class="rule-badges">
              <el-tag
                v-for="(rule, i) in normalizeRules(instrument.timeoutSetting)"
                :key="i"
                size="small"
                effect="light"
                type="info"
              >
                <el-tooltip :content="rule.rangeFull" placement="top">
                  <span>{{ rule.label }}</span>
                </el-tooltip>
              </el-tag>
            </el-space>
          </div>

          <!-- 日期卡片容器：橫向排列 -->
          <div class="date-card-container">
            <div
              v-for="date in instrument.dateList"
              :key="date.id"
              class="date-card-wrapper"
            >
              <el-card
                class="date-card"
                :class="date.background" 
                :body-style="{ padding: '10px 12px' }"
              >
                <el-tooltip
                  :content="date.type === 'day' ? date.date : date.tip"
                  placement="top"
                >
                  <span class="date-text">
                    {{ date.type === 'day' ? date.date : `${date.start_date} ~ ${date.end_date}` }}
                  </span>
                </el-tooltip>
              </el-card>
            </div>
          </div>
        </div>

      </el-card>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

export default {
  props:{
    areaData: Array,
    areaList: Array,
  },
  data() {
    return {
      stationTable: [],
      activeTimeType: 'thisMonth',
      activeTime: [
        new Date(new Date().getFullYear(), new Date().getMonth(), 1, 0, 0, 0, 0),
        new Date(),
      ],
      areafilters: [],
      areafiltersSelection: '',
    };
  },
  watch: {
    areaData(newVal){
      if (newVal) {
        this.loadStation();
      }
    },
    areaList(newVal){
      if (newVal) {
        this.loadArea();
      }
    },
    activeTime(newVal) {
      if (newVal) {
        this.loadStation();
      }
    },
    areafiltersSelection() {
      this.loadStation();
    },
  },
  mounted(){
    this.loadArea();
    this.loadStation();
  },
  methods: {
    setTime(timeType) {
      this.activeTimeType = timeType;
      const now = new Date();

      if (timeType === 'thisMonth') {
        const start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0); // 本月1號0點
        const end = new Date(); // 現在時間
        this.activeTime = [start, end];
      }

      else if (timeType === 'lastMonth') {
        const start = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0); // 上個月1號0點
        const end = new Date(new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0) - 1); // 本月1號0點 - 1ms
        this.activeTime = [start, end];
      }

      else {
        //console.warn(`Unknown timeType: ${timeType}`);
        this.timeType = 'custom';
      }
    },
    loadStation() {
      const currentArea = (this.areaData || []).find(a => a.areaName === this.areafiltersSelection);
      if(!currentArea || !currentArea.stations) return;

      this.stationTable = currentArea.stations.map(station=>{
        return {
          stationName: station.stationName,
          instruments: station.instruments.map(ins=>{
            let dateList = [];
            for(let timeoutSetting of ins.timeoutSetting){
              const { settingType, frequency_unit, frequency } = timeoutSetting;
              const timeout_start_date = timeoutSetting.start_date;
              const timeout_end_date   = timeoutSetting.end_date;
              let start_date = this.activeTime[0] > new Date(timeout_start_date) ? this.toLocalDateOnly(this.activeTime[0]) : timeout_start_date;
              let end_date   = this.activeTime[1] < new Date(timeout_end_date) ? this.toLocalDateOnly(this.activeTime[1]) : timeout_end_date;
              let outputs_range = this.filterDatesBetween(ins.outputs.map(ele=>ele.TIMESTAMP), start_date, end_date);
              if(frequency_unit === 'week'){
                let weeks = this.buildWeekList(outputs_range, start_date, end_date);
                let _dateList = weeks.map(output=>{
                  let isMet = output.count >= frequency;
                  return {
                    type: 'range',
                    start_date: output.dates[0],
                    end_date:   output.dates[output.dates.length - 1],
                    background: (isMet) ? 'bg-white' : 'bg-red',
                    tip: isMet ? `於${output.includesDates}進行監測` : '期間內未滿足監測頻率',
                  };
                });
                dateList.push(..._dateList);
              }
              else if(frequency_unit ==='month'){
                let months = this.buildMonthList(outputs_range, start_date, end_date);
                let _dateList = months.map(output=>{
                  let isMet = output.count >= frequency;
                  return {
                    type: 'range',
                    start_date: output.start,
                    end_date:   output.end,
                    background: (isMet) ? 'bg-white' : 'bg-red',
                    tip: isMet ? `於${output.includesDates}進行監測` : '期間內未更新',
                  };
                });
                dateList.push(..._dateList);
              }
              else{
                let count = 0;
                for (let cur = new Date(start_date); cur <= new Date(end_date); cur = this.addDays(cur, 1)){
                  let now_output = outputs_range[count];
                  let isMet = cur == new Date(now_output);
                  if(isMet) count++;
                  dateList.push({
                    type: 'day',
                    start_date: cur,
                    end_date:   cur,
                    background: (isMet) ? 'bg-white' : 'bg-red',
                    tip: isMet ? `於${now_output}進行監測` : `${now_output}未更新`,
                  });
                }
              }
            }

            return {
              ...ins,
              dateList,
            };
          }),
        };
      });
    },

    loadArea() {
      if(!this.areaList || this.areaList.length == 0) return;

      this.areafilters = this.areaList.map(ele=>{
        return {
          areaName: ele,
          //select: false,
        };
      });

      //this.areafilters[0].select = true;
      this.areafiltersSelection = this.areafilters[0].areaName;
    },

    /**
     * Node.js: 由日期陣列與起訖時間產生週列表（以星期一為週起始）
     * - 輸入日期可為 'YYYY-MM-DD' 或 Date 物件（可混用）
     * - 週物件包含 dates(一週7天)、monday..sunday 與 count
     */
    buildWeekList(dateArray, startDateInput = undefined, endDateInput = undefined) {
      if (!Array.isArray(dateArray)) {
        throw new Error('dateArray 必須是陣列');
      }

      // ---- 工具函式 (以「本地時區」處理日曆日，避免時間偏移) ----
      const pad2 = (n) => String(n).padStart(2, '0');
      const toYMD = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

      // ---- 正規化輸入日期陣列 ----
      const normalizedDates = dateArray.map(this.toLocalDateOnly);
      // 供計數使用的查表，把每個輸入日期轉成YMD計次數（同日出現多次要各自計數，所以記錄全部清單即可）
      const normalizedYMDs = normalizedDates.map(toYMD);

      // ---- 決定起訖時間 ----
      let startDate = startDateInput ? this.toLocalDateOnly(startDateInput) : undefined;
      let endDate   = endDateInput   ? this.toLocalDateOnly(endDateInput)   : undefined;

      if (!startDate) {
        if (normalizedDates.length === 0) {
          throw new Error('未提供起始時間且日期陣列為空，無法推定起始時間。請提供 startDate 或至少一個日期。');
        }
        // 取陣列中最早日期
        startDate = new Date(Math.min(...normalizedDates.map(d => d.getTime())));
      }
      if (!endDate) {
        const now = new Date();
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate()); // 今日
      }

      // 若起始 > 結束，交換或直接回傳空陣列
      if (startDate > endDate) {
        return [];
      }

      // 取覆蓋整段區間的第一個「週一」與最後一個「週日」
      const firstMonday = this.startOfWeekMonday(startDate);
      const lastSunday  = this.addDays(this.startOfWeekMonday(endDate), 6);

      const weeks = [];
      // 以週為步進
      for (let curMon = firstMonday; curMon <= lastSunday; curMon = this.addDays(curMon, 7)) {
        const days = Array.from({ length: 7 }, (_, i) => this.addDays(curMon, i));
        const daysYMD = days.map(toYMD);

        // 統計這一週內出現於輸入日期陣列中的筆數（同日多次出現就多次計）
        let count = 0;
        let includesDates = [];
        for (const y of normalizedYMDs) {
          if (daysYMD.includes(y)) {
            count += 1;
            includesDates.push(y);
          }
        }

        const [mon, tue, wed, thu, fri, sat, sun] = daysYMD;

        weeks.push({
          dates: daysYMD,
          includesDates,
          monday: mon,
          tuesday: tue,
          wednesday: wed,
          thursday: thu,
          friday: fri,
          saturday: sat,
          sunday: sun,
          count,
        });
      }

      // 僅保留與使用者要求的起訖範圍相交的週（題意：輸出的 week list 為介於起訖的所有週）
      // 上面已用 firstMonday/lastSunday 覆蓋整段；如果你想嚴格只要交集，可保留全部（通常需求即為全部）
      return weeks;
    },

    buildMonthList(dateArray, startDateInput = undefined, endDateInput = undefined) {
      if (!Array.isArray(dateArray)) {
        throw new Error('dateArray 必須是陣列');
      }

      // 工具
      const pad2 = (n) => String(n).padStart(2, '0');
      const toYMD = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
      const toYM  = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;

      function startOfMonth(d) {
        return new Date(d.getFullYear(), d.getMonth(), 1);
      }
      function endOfMonth(d) {
        return new Date(d.getFullYear(), d.getMonth() + 1, 0);
      }
      function addMonths(d, months) {
        const nd = new Date(d.getFullYear(), d.getMonth() + months, 1);
        return nd;
      }

      // 正規化輸入
      const normalizedDates = dateArray.map(this.toLocalDateOnly);

      // 起訖決定
      let startDate = startDateInput ? this.toLocalDateOnly(startDateInput) : undefined;
      let endDate   = endDateInput   ? this.toLocalDateOnly(endDateInput)   : undefined;

      if (!startDate) {
        if (normalizedDates.length === 0) {
          throw new Error('未提供起始時間且日期陣列為空，無法推定起始時間。請提供 startDate 或至少一個日期。');
        }
        startDate = new Date(Math.min(...normalizedDates.map(d => d.getTime())));
      }
      if (!endDate) {
        const now = new Date();
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      }
      if (startDate > endDate) return [];

      // 取完整月份邊界
      const firstMonth = startOfMonth(startDate);
      const lastMonthEnd = endOfMonth(endDate);
      const lastMonth = startOfMonth(lastMonthEnd);

      // 預先把輸入日期按「YYYY-MM」分組計數（同一天多次出現就多次計）
      const monthCount = new Map(); // key: 'YYYY-MM' → value: includesDates
      for (const d of normalizedDates) {
        const key = toYM(d);
        if(!monthCount.get(key)){
          monthCount.set(key, [d]);
        }
        else monthCount.get(key).push(d);
      }
      // 產生月份清單
      const months = [];
      for (let cur = new Date(firstMonth); cur <= lastMonth; cur = addMonths(cur, 1)) {
        const ym = toYM(cur);
        const start = startOfMonth(cur);
        const end = endOfMonth(cur);
        months.push({
          includesDates: monthCount.get(ym),
          month: ym,                 // 'YYYY-MM'
          start: toYMD(start),       // 該月第一天
          end: toYMD(end),           // 該月最後一天
          count: monthCount.get(ym)?.length || 0,
          dates: Array.from({length: end.getDate()}, (_, i) => toYMD(new Date(cur.getFullYear(), cur.getMonth(), i+1))),
        });
      }

      return months;
    },

    addDays(d, days) {
      const nd = new Date(d);
      nd.setDate(nd.getDate() + days);
      return nd;
    },

    // 以星期一為週起點的週一日期
    // JS: getDay() => 0(日)~6(六)；我們要將星期一視為0
    startOfWeekMonday(d) {
      const dow = d.getDay();                  // 0(日)~6(六)
      const shift = (dow + 6) % 7;             // 週一=0, 週二=1, ... 週日=6
      return this.addDays(d, -shift);
    },

    /**
     * 傳回 dateList 中介於 start_date 與 end_date（含邊界）的所有日期
     * 支援 'YYYY-MM-DD' 字串或 Date 物件混用
     */
    filterDatesBetween(dateList, startDate, endDate) {
      if (!Array.isArray(dateList)) throw new Error('dateList 必須是陣列');

      const start = this.toLocalDateOnly(startDate);
      const end = this.toLocalDateOnly(endDate);

      return dateList
        .map(this.toLocalDateOnly)
        .filter(d => d >= start && d <= end)
        .map(d => d.toISOString().slice(0, 10)); // 輸出成 'YYYY-MM-DD'
    },

    toLocalDateOnly(input) {
      if (input instanceof Date)
        return new Date(input.getFullYear(), input.getMonth(), input.getDate());
      const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(input.trim());
      if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
      const d = new Date(input);
      if (isNaN(d)) throw new Error(`無法解析日期：${input}`);
      return new Date(d.getFullYear(), d.getMonth(), d.getDate());
    },

    normalizeRules(settings = []) {
      const pad2 = (n) => String(n).padStart(2, '0');
      const toYMD = (d) => {
        const dd = (d instanceof Date) ? d : new Date(d);
        return `${dd.getFullYear()}-${pad2(dd.getMonth()+1)}-${pad2(dd.getDate())}`;
      };

      return settings.map(s => {
        const unitLabel =
          s.frequency_unit === 'week'  ? '每週'  :
            s.frequency_unit === 'month' ? '每月'  :
              s.frequency_unit || ''; // fallback

        const label = `${unitLabel} ≥ ${s.frequency} 次`;

        const rangeFull = `${toYMD(s.start_date)} ~ ${toYMD(s.end_date)}`;

        // 介面上精簡版（不想太長，可換成 YYYY-MM）
        const rangeShort = `${toYMD(s.start_date)}~${toYMD(s.end_date)}`;

        return { label: `${label}｜${rangeShort}`, rangeFull };
      });
    },

    async exportReport() {
      const root = this.$refs.exportRoot;
      if (!root) {
        this.$message.error('找不到要匯出的內容容器');
        return;
      }

      const cards = root.querySelectorAll('.revenue-card');
      if (!cards.length) {
        this.$message.warning('目前沒有可以匯出的區塊');
        return;
      }

      const loading = this.$loading({ lock: true, text: '匯出中…', background: 'rgba(0,0,0,.1)' });
      try {
        const pdf = new jsPDF('p', 'mm', 'a4'); 
        const pageW = pdf.internal.pageSize.getWidth(); 
        const pageH = pdf.internal.pageSize.getHeight(); 
        const margin = 8; // mm 

        const gap = 6;                 // 卡片之間的垂直間距 (mm)
        let curY = margin;             // 目前頁面的 Y 游標（從上邊距開始）
        let pageIndex = 0;

        for (const card of cards) {
          card.scrollIntoView({ block: 'center' });

          const canvas = await html2canvas(card, {
            scale: 2,
            useCORS: true,
            backgroundColor: '#ffffff',
          });

          // 依 A4 寬度等比縮放
          const imgW = pageW - margin * 2;
          const imgH = (canvas.height * imgW) / canvas.width;

          // 單張卡片若比頁面高度還高，等比再縮到單頁可容納高度
          const maxH = pageH - margin * 2;
          const scale = imgH > maxH ? maxH / imgH : 1;
          const drawW = imgW * scale;
          const drawH = imgH * scale;

          // 若本頁剩餘空間放不下，先換新頁並把游標重置到上邊距
          if (curY + drawH > pageH - margin) {
            if (pageIndex > 0 || curY > margin) pdf.addPage(); // 第一頁已建立；其餘才 addPage
            else if (pageIndex === 0 && curY === margin) {
              // 什麼都不做，代表是第一頁第一次要畫
            } else {
              pdf.addPage();
            }
            pageIndex++;
            curY = margin;
          }

          const img = canvas.toDataURL('image/jpeg', 0.95);
          pdf.addImage(img, 'JPEG', margin, curY, drawW, drawH);

          // 游標往下推，預留間距
          curY += drawH + gap;
        }

        pdf.save(`報表_${new Date().toISOString().slice(0,10)}.pdf`);
        this.$message.success('匯出完成');
      } catch (err) {
        console.error(err);
        this.$message.error('匯出失敗');
      } finally {
        loading.close();
      }
    },
  },
};
</script>

<style scoped>
.dashboard-container {
  padding: 24px;
  background-color: #f5f7fa; /* 淺灰色背景 */
  min-height: 100vh;
}

.filter-card {
  border: none;
}

.execute-button-col {
  text-align: right;
}

.revenue-card .instrument-list {
  margin: 18px 18px 20px;
}

.revenue-card :deep(.el-card__header) {
  padding: 16px 20px;
  background: #fafafa;
  border-bottom: 1px solid #f0f2f5;
}
.revenue-card :deep(.el-card__body) {
  padding: 16px 12px;   /* 與卡片 grid 的 gap 視覺協調 */
}

/* 日期卡片容器：橫向排列並允許換行 */
.date-card-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); /* 180 可微調 */
  gap: 12px;  /* 卡片間距 */
}

/* 控制每個日期卡片的外觀 */
.date-card :deep(.el-card__body) {
  padding: 10px 12px;   /* 緊湊一點 */
}
.date-card {
  min-height: 44px;     /* 固定視覺高度，整齊 */
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  line-height: 1.2;
  font-size: 13px;
}

.date-text {
  display: inline-block;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.instrument-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px; /* 與下方卡片群保持距離 */
}

.rule-badges :deep(.el-tag) {
  border-color: #d9e3f7;
  color: #3a62a6;
  background: #f3f7ff;
  font-weight: 500;
}

.instrument-name {
  font-weight: 600;
  margin-bottom: 10px;
  color: #303133;
  border-left: 3px solid #409EFF;
  padding-left: 8px;
}

.bg-red {
  background-color: #ffb4b4; /* 淡紅色 */
  border: 1px solid #f5a2a0;
}

.bg-white {
  background-color: #ffffff;
  border: 1px solid #ebeef5;
}


/* 響應式調整 */
@media (max-width: 768px) {
  .execute-button-col {
    text-align: left;
    margin-top: 15px;
  }
  .time-filters {
    display: flex;
    width: 100%;
  }
  .time-filters .el-button {
    flex-grow: 1;
  }
  .el-date-picker {
    width: 100%;
    margin-left: 0 !important;
    margin-top: 10px;
  }
}
</style>